import React, { ChangeEvent, JSX, useState, useEffect } from 'react';
import {
  Alert,
  TabController,
  TabList,
  TabPanel,
  Tab,
  Typography,
} from '@one-thd/sui-atomic-components';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import {
  QueryProvider,
  params,
  shape,
  string as stringType,
  number as numberType,
  useDataModel,
  extend,
} from '@thd-nucleus/data-sources';
import {
  useAccountCrumbs,
  CustomerAccountNavigation,
} from '@thd-olt-component-react/customer-account-navigation';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import PageLayout from '../components/shared/PageLayout';
import { DownloadsTableWithTabsSkeleton } from '../components/downloads/DownloadsSkeletons';
import { PurchaseHistoryDownloads } from '../components/downloads/purchase-history-downloads/PurchaseHistoryDownloads';
import { LinkedAccountDownloads } from '../components/downloads/linked-accounts-downloads/LinkedAccountDownloads';
import DownloadProvider from '../contexts/DownloadsContext';
import { clearRedirectFlags } from '../utils/linked-accounts';
import { getFeatureSwitchValue, getWindow } from '../utils/general';

interface DownloadProps {
  customerType: string;
  customerRole: string;
}

interface IDownloadTabs {
  currentTab: string;
  handleChange: (event: ChangeEvent, value: string) => void;
}

const DownloadTabs = ({
  currentTab,
  handleChange,
}: IDownloadTabs): JSX.Element => {
  const [showLinkedAccounts, setShowLinkedAccounts] = useState(false);
  const { userID, isB2BCustomer } = useThdCustomer();

  const { data } = useDataModel('userState', {
    variables: { userId: userID, channelId: 1 },
    skip: !isB2BCustomer || !userID,
    ssr: false,
    context: { withAuth: true },
  });

  useEffect(() => {
    if (
      data?.userState.customer?.hierarchyOrgId ||
      data?.userState.customer?.companyId
    ) {
      setShowLinkedAccounts(true);
    }
  }, [data]);

  return data || data === undefined ? (
    <TabController value={currentTab}>
      <TabList onChange={handleChange} aria-label="download page tabs">
        <Tab label="Purchase History" value="Purchase History" />
        {showLinkedAccounts && (
          <Tab label="Linked Account" value="Linked Account" />
        )}
      </TabList>
      <TabPanel value="Purchase History">
        <ErrorBoundary name="purchase-history-downloads">
          <PurchaseHistoryDownloads />
        </ErrorBoundary>
      </TabPanel>
      <TabPanel value="Linked Account">
        <ErrorBoundary name="linked-account-downloads">
          <LinkedAccountDownloads />
        </ErrorBoundary>
      </TabPanel>
    </TabController>
  ) : (
    <DownloadsTableWithTabsSkeleton />
  );
};

export const Downloads = (props: DownloadProps) => {
  const { customerType, customerRole } = props;
  const [showAlert, setShowAlert] = useState(false);

  const crumbs = useAccountCrumbs([
    {
      label: 'Downloads',
      path: '/myaccount/downloads',
    },
  ]);

  /**
   * On page render we have to check if the user started the download and redirected to
   * this page.  This is because we have to show the tab of the respected page it came from.
   *
   * Set sessionStorage for checking redirect, 'linkedAccountsRedirect' is used for
   * redirection from linked accounts page.
   *
   */

  const currentTabName = getWindow()?.sessionStorage.getItem(
    'linkedAccountsRedirect',
  )
    ? 'Linked Account'
    : 'Purchase History';
  const [currentTab, setCurrentTab] = useState(currentTabName);

  const msg = `Download Initiated! You can view the status of your file below.
    Depending on data size, download times may be longer.
    `;
  const phOfflineDownloadThreshold =
    (getFeatureSwitchValue('phOfflineDownloadThreshold') as number) || 4000;

  useEffect(() => {
    const redirectFromFlag =
      getWindow()?.sessionStorage.getItem('linkedAccountsRedirect') ||
      getWindow()?.sessionStorage.getItem('orderHistoryRedirect');

    if (redirectFromFlag) {
      setShowAlert(true);
      clearRedirectFlags();
    }
  }, []);

  const handleChange = (_event: ChangeEvent, newValue: string) => {
    setCurrentTab(newValue);
  };

  return (
    <PageLayout
      crumbs={crumbs}
      pageTitle="Downloads"
      customerRole={customerRole}
      customerType={customerType}
      internalRoutes={['/myaccount/downloads']}
    >
      <QueryProvider cacheKey="customer-account-navigation">
        <DownloadProvider>
          <div className="sui-w-full sui-max-w-[1039px]">
            <div className="sui-flex sui-flex-col sui-w-full sui-gap-3 sui-mt-2 sui-mb-3">
              <Typography variant="h1">Downloads</Typography>
              <Typography variant="body-base">
                You can access and download all your Purchase History exports
                containing over {phOfflineDownloadThreshold} rows of data below.
              </Typography>
            </div>
            {showAlert && (
              <Alert status="info" size="large">
                {msg}
              </Alert>
            )}
            <DownloadTabs currentTab={currentTab} handleChange={handleChange} />
          </div>
        </DownloadProvider>
      </QueryProvider>
    </PageLayout>
  );
};

Downloads.dataModel = extend(
  {
    loyaltyMembershipDetails: params({
      userId: stringType().isRequired(),
      customerAccountId: stringType().isRequired(),
      channelId: numberType().isRequired(),
    }).shape({
      userState: params({
        userId: stringType().isRequired(),
        channelId: numberType().isRequired(),
      }).shape({
        customer: shape({
          hierarchyOrgId: stringType(),
          companyId: stringType(),
        }),
      }),
    }),
  },
  CustomerAccountNavigation,
);

Downloads.displayName = 'Downloads';
